import React, { useMemo, useRef, useCallback, useState } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker, InfoWindow } from '@react-google-maps/api';
import { Box, CircularProgress, Typography } from '@material-ui/core';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['geometry'];

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: 'greedy',
  clickableIcons: false,
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

// Decode standard Google polyline format (fallback when geometry library unavailable).
function decodePolyline(encoded) {
  if (!encoded) return [];
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let b; let shift = 0; let result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;
    points.push({ lat: lat * 1e-5, lng: lng * 1e-5 });
  }
  return points;
}

function extractPolylinePath(route) {
  const rd = route?.routeData || {};
  const cr = rd.computedRoute || {};
  if (typeof cr.polyline === 'string' && cr.polyline.length > 0) {
    try { return decodePolyline(cr.polyline); } catch { /* ignore */ }
  }
  if (cr.geometry?.coordinates && Array.isArray(cr.geometry.coordinates)) {
    return cr.geometry.coordinates.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
  }
  if (Array.isArray(cr.sections) && cr.sections.length && window.google?.maps?.geometry?.encoding) {
    try {
      const pts = [];
      for (const s of cr.sections) {
        if (typeof s.polyline === 'string') {
          const decoded = window.google.maps.geometry.encoding.decodePath(s.polyline);
          pts.push(...decoded.map(p => ({ lat: p.lat(), lng: p.lng() })));
        }
      }
      if (pts.length) return pts;
    } catch { /* ignore */ }
  }
  const pts = [];
  if (route.origin?.coordinates) pts.push({ lat: route.origin.coordinates[1], lng: route.origin.coordinates[0] });
  (route.waypoints || []).forEach(wp => {
    if (wp.coordinates) pts.push({ lat: wp.coordinates[1], lng: wp.coordinates[0] });
  });
  if (route.destination?.coordinates) pts.push({ lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] });
  return pts;
}

// Semantic styling for each stop kind. Origin/destination use A/B labels,
// intermediate stops use their sequence number. Colour carries the pickup vs
// service/delivery distinction at a glance.
function pointStyleFor(kind, label) {
  const base = {
    pickup:   { color: '#16a34a', emoji: '▲', labelHint: 'Recolha' },
    delivery: { color: '#dc2626', emoji: '▼', labelHint: 'Entrega' },
    service:  { color: '#0f172a', emoji: '🔧', labelHint: 'Serviço' },
    fuel:     { color: '#f59e0b', emoji: '⛽', labelHint: 'Combustível' },
    rest:     { color: '#7c3aed', emoji: '🛌', labelHint: 'Pausa' },
    origin:   { color: '#16a34a', emoji: 'A',  labelHint: 'Saída' },
    destination: { color: '#dc2626', emoji: 'B', labelHint: 'Destino' },
    default:  { color: '#2563eb', emoji: '●', labelHint: 'Paragem' },
  };
  const cfg = base[kind] || base.default;
  return { ...cfg, label: label || cfg.emoji };
}

function fmtDuration(seconds) {
  if (!seconds) return '—';
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
}

function fmtDistance(meters) {
  if (!meters) return '—';
  if (meters < 1000) return `${meters} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

// Format a Date as local HH:MM (24h).
function fmtClock(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

// Detect likely mobile platform so we can pick the best nav URL scheme.
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function buildNavigationUrls(lat, lng, label) {
  const safeLabel = encodeURIComponent(label || '');
  return {
    // Waze URL scheme. Works on mobile (opens app) and web (waze.com).
    waze: `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`,
    // Google Maps: universal link with turn-by-turn.
    google: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}${safeLabel ? `&destination_name=${safeLabel}` : ''}&travelmode=driving`,
    // Apple Maps (iOS only). Falls back to Google on Android.
    apple: `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`,
  };
}

export default function DriverRouteMap({ route, height = 240 }) {
  const mapRef = useRef(null);
  const [activeMarker, setActiveMarker] = useState(null); // { key } | null

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  const path = useMemo(() => extractPolylinePath(route || {}), [route]);

  // Cumulative drive time (seconds) from origin up to each stop. Uses the
  // HERE "sections" array — one section per leg. Falls back to equal splits
  // of the total duration when the per-leg breakdown isn't available.
  const cumulativeLegDurations = useMemo(() => {
    const sections = route?.routeData?.computedRoute?.sections || [];
    const totalStops = (route?.waypoints?.length || 0) + (route?.destination ? 1 : 0);
    if (sections.length > 0 && sections.length === totalStops) {
      let acc = 0;
      return sections.map(s => {
        acc += Number(s?.summary?.duration || 0);
        return acc;
      });
    }
    // Fallback: even split
    const total = Number(route?.totalDurationS || route?.routeData?.computedRoute?.duration || 0);
    if (totalStops === 0) return [];
    const per = total / totalStops;
    return Array.from({ length: totalStops }, (_, i) => Math.round(per * (i + 1)));
  }, [route]);

  // Expected ETA (Date) per stop, computed off departureTime (or now if not set)
  // and accumulated driving + stop durations.
  const stopETAs = useMemo(() => {
    const startMs = route?.departureTime ? new Date(route.departureTime).getTime() : Date.now();
    const etas = [];
    let acc = 0; // accumulated stop-duration seconds so far
    const wps = route?.waypoints || [];
    for (let i = 0; i < wps.length; i++) {
      const driveToHere = cumulativeLegDurations[i] || 0;
      etas.push(new Date(startMs + (driveToHere + acc) * 1000));
      acc += Number(wps[i]?.stopDuration || 0) * 60; // stopDuration is minutes
    }
    if (route?.destination) {
      const driveToDest = cumulativeLegDurations[wps.length] || 0;
      etas.push(new Date(startMs + (driveToDest + acc) * 1000));
    }
    return etas;
  }, [route, cumulativeLegDurations]);

  // Build a structured marker list with rich metadata (kind, order, stop duration, ETA, OT ref).
  const markers = useMemo(() => {
    const list = [];
    // Origin — always index 0
    if (route?.origin?.coordinates) {
      list.push({
        key: 'origin',
        position: { lat: route.origin.coordinates[1], lng: route.origin.coordinates[0] },
        kind: 'origin',
        label: 'A',
        title: route.origin.name || 'Saída',
        meta: {
          orderNumber: route.sourceOT?.orderNumber || null,
          eta: route.departureTime ? new Date(route.departureTime) : null,
          etaLabel: 'Partida',
        },
      });
    }
    (route?.waypoints || []).forEach((wp, i) => {
      if (!wp.coordinates) return;
      list.push({
        key: `wp-${i}`,
        position: { lat: wp.coordinates[1], lng: wp.coordinates[0] },
        kind: wp.waypointKind || 'default',
        label: String(i + 1),
        title: wp.name || `Paragem ${i + 1}`,
        meta: {
          orderNumber: wp.orderNumber,
          stopDuration: wp.stopDuration,
          agreedPrice: wp.agreedPrice,
          index: i + 1,
          eta: stopETAs[i] || null,
          driveFromStartS: cumulativeLegDurations[i] || null,
        },
      });
    });
    if (route?.destination?.coordinates) {
      list.push({
        key: 'dest',
        position: { lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] },
        kind: 'destination',
        label: 'B',
        title: route.destination.name || 'Destino',
        meta: {
          eta: stopETAs[(route.waypoints || []).length] || null,
          driveFromStartS: cumulativeLegDurations[(route.waypoints || []).length] || null,
        },
      });
    }
    return list;
  }, [route, stopETAs, cumulativeLegDurations]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (path.length === 0 && markers.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    markers.forEach(m => bounds.extend(m.position));
    map.fitBounds(bounds, { top: 40, bottom: 24, left: 24, right: 24 });
  }, [path, markers]);

  // Pan+zoom to a clicked marker and open its info window.
  const focusMarker = useCallback((marker) => {
    setActiveMarker(marker.key);
    if (mapRef.current) {
      mapRef.current.panTo(marker.position);
      const cur = mapRef.current.getZoom?.() || 10;
      if (cur < 12) mapRef.current.setZoom(12);
    }
  }, []);

  // Route-level summary (distance, duration, stops)
  const summary = useMemo(() => {
    const distanceM = route?.totalDistanceM ?? route?.routeData?.computedRoute?.distance;
    const durationS = route?.totalDurationS ?? route?.routeData?.computedRoute?.duration;
    const stopsCount = (route?.waypoints?.length || 0) + (route?.destination ? 1 : 0);
    return { distanceM, durationS, stopsCount };
  }, [route]);

  if (loadError) {
    return (
      <Box style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
        <Typography style={{ fontSize: '0.8rem', color: '#64748b' }}>Erro ao carregar mapa</Typography>
      </Box>
    );
  }
  if (!isLoaded) {
    return (
      <Box style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const center = markers[0]?.position || { lat: 39.5, lng: -8 };

  return (
    <Box style={{ position: 'relative' }}>
      {/* Summary chip overlay — floats on top of the map */}
      <Box style={{
        position: 'absolute', top: 8, left: 8, right: 8, zIndex: 5,
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        pointerEvents: 'none',
      }}>
        <SummaryChip icon="🛣" label={fmtDistance(summary.distanceM)} />
        <SummaryChip icon="⏱" label={fmtDuration(summary.durationS)} />
        <SummaryChip icon="📍" label={`${summary.stopsCount} ${summary.stopsCount === 1 ? 'paragem' : 'paragens'}`} />
      </Box>

      <Box style={{ height }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={8}
          options={MAP_OPTIONS}
          onLoad={onMapLoad}
          onClick={() => setActiveMarker(null)}
        >
          {path.length > 1 && (
            <>
              {/* White halo under the route for better contrast on satellite/low-zoom views */}
              <Polyline
                path={path}
                options={{
                  strokeColor: '#ffffff',
                  strokeOpacity: 0.9,
                  strokeWeight: 4,
                  geodesic: false,
                  zIndex: 1,
                }}
              />
              <Polyline
                path={path}
                options={{
                  strokeColor: '#0f172a',
                  strokeOpacity: 0.95,
                  strokeWeight: 2,
                  geodesic: false,
                  zIndex: 2,
                }}
              />
            </>
          )}
          {markers.map(m => {
            const style = pointStyleFor(m.kind, m.label);
            const isActive = activeMarker === m.key;
            return (
              <React.Fragment key={m.key}>
                <Marker
                  position={m.position}
                  title={m.title}
                  onClick={() => focusMarker(m)}
                  label={{
                    text: m.label,
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: '700',
                  }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: style.color,
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: isActive ? 3 : 2,
                    scale: isActive ? 13 : 11,
                  }}
                  zIndex={isActive ? 10 : 3}
                />
                {isActive && (
                  <InfoWindow
                    position={m.position}
                    options={{ pixelOffset: new window.google.maps.Size(0, -18) }}
                    onCloseClick={() => setActiveMarker(null)}
                  >
                    <Box style={{ minWidth: 200, maxWidth: 260, padding: '2px 4px' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Box style={{
                          width: 20, height: 20, borderRadius: 6,
                          background: style.color, color: '#fff',
                          fontSize: '0.62rem', fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          {m.label}
                        </Box>
                        <Typography style={{ fontSize: '0.64rem', fontWeight: 700, color: style.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {style.labelHint}
                        </Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
                        {m.title}
                      </Typography>
                      {m.meta?.orderNumber && (
                        <Typography style={{ fontSize: '0.64rem', color: '#64748b', marginTop: 2 }}>
                          🧾 {m.meta.orderNumber}
                        </Typography>
                      )}
                      {/* ETA + driving time block */}
                      {(m.meta?.eta || m.meta?.driveFromStartS != null) && (
                        <Box style={{
                          display: 'flex', gap: 8, marginTop: 6, padding: '5px 8px',
                          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
                        }}>
                          {m.meta?.eta && (
                            <Box>
                              <Typography style={{ fontSize: '0.52rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {m.meta.etaLabel || 'Chegada'}
                              </Typography>
                              <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
                                {fmtClock(m.meta.eta)}
                              </Typography>
                            </Box>
                          )}
                          {m.meta?.driveFromStartS != null && (
                            <Box>
                              <Typography style={{ fontSize: '0.52rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Desde saída
                              </Typography>
                              <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: '#334155', lineHeight: 1.1 }}>
                                {fmtDuration(m.meta.driveFromStartS)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {m.meta?.stopDuration != null && m.meta.stopDuration > 0 && (
                        <Typography style={{ fontSize: '0.66rem', color: '#334155', marginTop: 4 }}>
                          ⏱ {m.meta.stopDuration} min no local
                        </Typography>
                      )}
                      {/* Navigation buttons — open Waze / Google Maps / Apple Maps */}
                      {m.kind !== 'origin' && (() => {
                        const urls = buildNavigationUrls(m.position.lat, m.position.lng, m.title);
                        const btn = (url, bg, fg, label) => (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', flex: 1 }}>
                            <Box style={{
                              padding: '6px 8px', borderRadius: 6, textAlign: 'center',
                              background: bg, color: fg, fontSize: '0.66rem', fontWeight: 700,
                              border: `1px solid ${fg}22`,
                              cursor: 'pointer',
                            }}>
                              {label}
                            </Box>
                          </a>
                        );
                        return (
                          <Box style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                            {btn(urls.waze, '#05d1f0', '#003f5c', 'Waze')}
                            {btn(urls.google, '#4285f4', '#fff', 'Google Maps')}
                            {isIOS() && btn(urls.apple, '#f1f5f9', '#0f172a', 'Apple Maps')}
                          </Box>
                        );
                      })()}
                    </Box>
                  </InfoWindow>
                )}
              </React.Fragment>
            );
          })}
        </GoogleMap>
      </Box>

      {/* Legend strip below the map — visual key for the driver */}
      <Box style={{
        display: 'flex', gap: 10, padding: '6px 10px',
        background: '#fff', borderTop: '1px solid #f1f5f9',
        fontSize: '0.62rem', color: '#64748b', flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        <LegendDot color="#16a34a" label="Saída" />
        <LegendDot color="#2563eb" label="Paragem" />
        <LegendDot color="#dc2626" label="Destino" />
        <Box style={{ color: '#94a3b8' }}>· toca nos pontos para ver detalhes</Box>
      </Box>
    </Box>
  );
}

function SummaryChip({ icon, label }) {
  return (
    <Box style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 9px', borderRadius: 14,
      background: 'rgba(255,255,255,0.95)',
      border: '1px solid rgba(15,23,42,0.08)',
      fontSize: '0.68rem', fontWeight: 600,
      color: '#0f172a',
      boxShadow: '0 2px 6px rgba(15,23,42,0.08)',
      pointerEvents: 'auto',
    }}>
      <span style={{ fontSize: '0.72rem' }}>{icon}</span>
      {label}
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Box style={{ width: 8, height: 8, borderRadius: '50%', background: color, border: '1px solid rgba(15,23,42,0.1)' }} />
      <span>{label}</span>
    </Box>
  );
}
